import { useEffect, useRef, useState } from 'react';

export type DropdownPosition = {
  top: number;
  left: number;
};

type UseDropdownPositionReturn = {
  open: boolean;
  setOpen: (open: boolean) => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  popoverRef: React.RefObject<HTMLDivElement | null>;
  position: DropdownPosition;
};

/**
 * Shared dropdown logic: click-outside detection, position calculation.
 * Keeps positioning consistent across DateRangePicker, UserFilterDropdown, CheckboxDropdown.
 */
export const useDropdownPosition = (positionOffset?: {
  x?: number;
  y?: number;
}): UseDropdownPositionReturn => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<DropdownPosition>({ top: 0, left: 0 });

  // Click-outside detection
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node) &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Position calculation
  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();

      setPosition({
        top: rect.bottom + (positionOffset?.y ?? 4),
        left: rect.left + (positionOffset?.x ?? 0),
      });
    }
  }, [open, positionOffset]);

  return {
    open,
    setOpen,
    buttonRef,
    popoverRef,
    position,
  };
};
